import { ReactNode, useEffect, useState } from "react";
import { cn } from "@/lib/utils";
import { prefersReducedMotion } from "@/lib/performance";

type TransitionType = "fade" | "slide-left" | "slide-right" | "slide-up" | "slide-down" | "scale";

interface ScreenTransitionProps {
  children: ReactNode;
  type?: TransitionType;
  duration?: number;
  delay?: number;
  className?: string;
}

export const ScreenTransition = ({
  children,
  type = "fade",
  duration = 300,
  delay = 0,
  className,
}: ScreenTransitionProps) => {
  const [isVisible, setIsVisible] = useState(false);
  const shouldAnimate = !prefersReducedMotion();

  useEffect(() => {
    const timer = setTimeout(() => {
      setIsVisible(true);
    }, delay);

    return () => clearTimeout(timer);
  }, [delay]);

  if (!shouldAnimate) {
    return <div className={className}>{children}</div>;
  }

  const transitions: Record<TransitionType, string> = {
    fade: "opacity-0 data-[visible=true]:opacity-100",
    "slide-left": "translate-x-full data-[visible=true]:translate-x-0",
    "slide-right": "-translate-x-full data-[visible=true]:translate-x-0",
    "slide-up": "translate-y-8 opacity-0 data-[visible=true]:translate-y-0 data-[visible=true]:opacity-100",
    "slide-down": "-translate-y-8 opacity-0 data-[visible=true]:translate-y-0 data-[visible=true]:opacity-100",
    scale: "scale-95 opacity-0 data-[visible=true]:scale-100 data-[visible=true]:opacity-100",
  };

  return (
    <div
      data-visible={isVisible}
      className={cn(
        "transition-all",
        transitions[type],
        className
      )}
      style={{
        transitionDuration: `${duration}ms`,
        transitionTimingFunction: "cubic-bezier(0.4, 0, 0.2, 1)",
      }}
    >
      {children}
    </div>
  );
};
